import { MainTableState } from '@/lib/utils/table-state-utils'

interface DrizzWhereCondition {
	table: string
	column: string
	operator: 'eq' | 'ilike' | 'gt' | 'lt' | 'gte' | 'lte' | 'in'
	value: any
}

interface DrizzOrderBy {
	table: string
	column: string
	direction: 'asc' | 'desc'
}

interface DrizzQuery {
	select: string[]
	from: string
	where: DrizzWhereCondition[]
	orderBy: DrizzOrderBy[]
	limit: number
	offset: number
	joins?: any[]
}

/**
 * Converts MainTableState to drizzQuery format for shipping rates API
 */
export function tableStateToDrizzQuery(
	tableState: MainTableState,
	storeId: string,
): { drizzQuery: DrizzQuery } {
	const { pagination, sorting, columnFilters } = tableState
	// Build where conditions from column filters
	const where: DrizzWhereCondition[] = columnFilters
		.map((filter) => {
			const { id, value } = filter
			const [table, column] = id.split('.')

			if (!table || !column) return undefined

			if (value && typeof value === 'object' && 'condition' in value) {
				const typedValue = value as { condition: string; value: any }

				switch (typedValue.condition) {
					case 'contains':
						return {
							table,
							column,
							operator: 'ilike' as const,
							value: `%${typedValue.value}%`,
						}
					case 'equals':
						return {
							table,
							column,
							operator: 'eq' as const,
							value: typedValue.value,
						}
					default:
						return {
							table,
							column,
							operator: 'eq' as const,
							value: typedValue.value,
						}
				}
			} else {
				return {
					table,
					column,
					operator: Array.isArray(value) && value.length > 1 ? ('in' as const) : ('eq' as const),
					value: Array.isArray(value) ? value : [value],
				}
			}
		})
		.filter((condition): condition is any => condition !== undefined)

	// Add storeId filter
	where.push({
		table: 'shippingRates',
		column: 'storeId',
		operator: 'eq',
		value: storeId,
	})

	// Build orderBy from sorting
	const orderBy: DrizzOrderBy[] =
		sorting.length > 0
			? sorting.map(({ id, desc }) => {
				const parts = id.split('.')
				// If id contains a dot, it's table.column format
				// Otherwise, it's just a column name
				if (parts.length > 1) {
					const [table, column] = parts
					return {
						table: table || 'shippingRates',
						column: column || id,
						direction: desc ? ('desc' as const) : ('asc' as const),
					}
				} else {
					// No table prefix, use default table
					return {
						table: 'shippingRates',
						column: id,
						direction: desc ? ('desc' as const) : ('asc' as const),
					}
				}
			})
			: [
				{
					table: 'shippingRates',
					column: 'createdAt',
					direction: 'desc' as const,
				},
			]

	return {
		drizzQuery: {
			select: ['id', 'shippingRateName', 'description', 'amount', 'storeId', 'createdAt', 'updatedAt'],
			from: 'shippingRates',
			where,
			orderBy,
			limit: pagination.pageSize,
			offset: pagination.pageIndex * pagination.pageSize,
			joins: [],
		},
	}
}

